import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import Layout from '../Components/Layout';
import 'react-toastify/dist/ReactToastify.css';

const CATEGORY_LABELS = {
    form_type: 'Form Type',
    order_type: 'Order Type',
    unit_type: 'Unit Type',
    bottle_type: 'Bottle Type',
    bottle_color: 'Bottle Color',
    closure_type: 'Closure Type',
    closure_color: 'Closure Color',
    cap_liner_type: 'Cap Liner Type',
    label_type: 'Label Type',
    deal_type: 'Deal Type',
    quote_priority: 'Quote Priority',
    complexity: 'Complexity',
    shipping_method: 'Shipping Method',
    packaging_type: 'Packaging Type',
};

const DropdownManager = () => {
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(
        () => localStorage.getItem('dropdownManagerSelectedCategory') || ''
    );
    const [values, setValues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({ value: '', label: '', sort_order: 0 });
    const [showAddForm, setShowAddForm] = useState(false);
    const [newItem, setNewItem] = useState({ value: '', label: '', sort_order: 0 });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        checkAdmin();
        fetchCategories();
    }, []);

    useEffect(() => {
        if (selectedCategory) {
            fetchValues(selectedCategory);
        }
    }, [selectedCategory]);

    const checkAdmin = async () => {
        try {
            const response = await axios.get('manage/check-role');
            if (response.data !== "Admin") {
                window.location.href = "/login";
            }
        } catch (error) {
            window.location.href = "/login";
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get('/admin/dropdowns/categories');
            const cats = response.data.categories || [];
            setCategories(cats);
            // Restore the previously-selected category if it still exists,
            // otherwise fall back to the first one. Persisted in localStorage
            // so a hard refresh keeps the user on the same category.
            const stored = localStorage.getItem('dropdownManagerSelectedCategory');
            const hasStored = stored && cats.some(c => c.category === stored);
            if (hasStored) {
                setSelectedCategory(stored);
            } else if (cats.length > 0) {
                setSelectedCategory(cats[0].category);
            }
            setLoading(false);
        } catch (error) {
            console.error('Error fetching categories:', error);
            toast.error('Failed to load dropdown categories');
            setLoading(false);
        }
    };

    const fetchValues = async (category) => {
        try {
            const response = await axios.get(`/admin/dropdowns?category=${category}`);
            setValues(response.data.values);
        } catch (error) {
            console.error('Error fetching values:', error);
            toast.error('Failed to load dropdown values');
        }
    };

    const handleEdit = (item) => {
        setEditingId(item.id);
        setEditForm({ value: item.value, label: item.label, sort_order: item.sort_order });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditForm({ value: '', label: '', sort_order: 0 });
    };

    const handleUpdate = async (id) => {
        setSaving(true);
        try {
            await axios.put(`/admin/dropdowns/${id}`, editForm);
            toast.success('Value updated successfully');
            setEditingId(null);
            fetchValues(selectedCategory);
            fetchCategories();
        } catch (error) {
            console.error('Error updating value:', error);
            toast.error('Failed to update value');
        } finally {
            setSaving(false);
        }
    };

    const handleAdd = async () => {
        if (!newItem.value.trim() || !newItem.label.trim()) {
            toast.warn('Value and Label are required');
            return;
        }
        setSaving(true);
        try {
            const response = await axios.post('/admin/dropdowns', {
                category: selectedCategory,
                value: newItem.value.trim(),
                label: newItem.label.trim(),
                sort_order: newItem.sort_order || 0
            });
            if (response.status === 409) {
                toast.warn(response.data.detail);
            } else {
                toast.success('Value added successfully');
                setNewItem({ value: '', label: '', sort_order: 0 });
                setShowAddForm(false);
                fetchValues(selectedCategory);
                fetchCategories();
            }
        } catch (error) {
            if (error.response && error.response.status === 409) {
                toast.warn(error.response.data.detail);
            } else {
                console.error('Error adding value:', error);
                toast.error('Failed to add value');
            }
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (item) => {
        const action = item.is_active ? 'deactivate' : 'activate';
        if (item.is_active) {
            const activeCount = values.filter(v => v.is_active).length;
            if (activeCount <= 1) {
                const confirmed = window.confirm(
                    'This is the last active value in this category. Deactivating it will leave no options available. Continue?'
                );
                if (!confirmed) return;
            } else {
                const confirmed = window.confirm(
                    'Deactivating this value will hide it from new records. Historical records will not be affected. Continue?'
                );
                if (!confirmed) return;
            }
        }
        try {
            await axios.put(`/admin/dropdowns/${item.id}/${action}`);
            toast.success(`Value ${action}d successfully`);
            fetchValues(selectedCategory);
            fetchCategories();
        } catch (error) {
            console.error(`Error ${action} value:`, error);
            toast.error(`Failed to ${action} value`);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="text-center py-5">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <ToastContainer />
            <div className="container-fluid py-4">
                <div className="row">
                    <div className="col-12">
                        <h4 className="mb-4">Dropdown Management</h4>
                    </div>
                </div>

                <div className="row">
                    {/* Category Sidebar */}
                    <div className="col-md-3">
                        <div className="card">
                            <div className="card-header">
                                <h6 className="mb-0">Categories</h6>
                            </div>
                            <div className="list-group list-group-flush">
                                {categories.map(cat => (
                                    <button
                                        key={cat.category}
                                        className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${selectedCategory === cat.category ? 'active' : ''}`}
                                        onClick={() => {
                                            setSelectedCategory(cat.category);
                                            localStorage.setItem('dropdownManagerSelectedCategory', cat.category);
                                            setShowAddForm(false);
                                            setEditingId(null);
                                        }}
                                    >
                                        <span>{CATEGORY_LABELS[cat.category] || cat.category}</span>
                                        <span className="badge bg-secondary rounded-pill">
                                            {cat.active_count}/{cat.count}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Values Table */}
                    <div className="col-md-9">
                        <div className="card">
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <h6 className="mb-0">
                                    {CATEGORY_LABELS[selectedCategory] || selectedCategory} Values
                                </h6>
                                {!showAddForm && (
                                    <button
                                        className="btn btn-sm btn-primary"
                                        onClick={() => {
                                            setShowAddForm(true);
                                            setNewItem({ value: '', label: '', sort_order: values.length + 1 });
                                        }}
                                    >
                                        + Add Value
                                    </button>
                                )}
                            </div>
                            <div className="card-body p-0">
                                <table className="table table-hover mb-0">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '60px' }}>Order</th>
                                            <th>Value</th>
                                            <th>Label</th>
                                            <th style={{ width: '100px' }}>Status</th>
                                            <th style={{ width: '150px' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {showAddForm && (
                                            <tr className="table-success">
                                                <td>
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm"
                                                        value={newItem.sort_order}
                                                        onChange={e => setNewItem({ ...newItem, sort_order: parseInt(e.target.value) || 0 })}
                                                        style={{ width: '60px' }}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        placeholder="Value (stored)"
                                                        value={newItem.value}
                                                        onChange={e => setNewItem({ ...newItem, value: e.target.value })}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        placeholder="Label (displayed)"
                                                        value={newItem.label}
                                                        onChange={e => setNewItem({ ...newItem, label: e.target.value })}
                                                    />
                                                </td>
                                                <td>
                                                    <span className="badge bg-success">New</span>
                                                </td>
                                                <td>
                                                    <button
                                                        className="btn btn-sm btn-success me-1"
                                                        onClick={handleAdd}
                                                        disabled={saving}
                                                    >
                                                        {saving ? '...' : 'Save'}
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-outline-secondary"
                                                        onClick={() => setShowAddForm(false)}
                                                    >
                                                        Cancel
                                                    </button>
                                                </td>
                                            </tr>
                                        )}
                                        {values.map(item => (
                                            <tr key={item.id} className={!item.is_active ? 'table-secondary' : ''}>
                                                <td>
                                                    {editingId === item.id ? (
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm"
                                                            value={editForm.sort_order}
                                                            onChange={e => setEditForm({ ...editForm, sort_order: parseInt(e.target.value) || 0 })}
                                                            style={{ width: '60px' }}
                                                        />
                                                    ) : (
                                                        item.sort_order
                                                    )}
                                                </td>
                                                <td>
                                                    {editingId === item.id ? (
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm"
                                                            value={editForm.value}
                                                            onChange={e => setEditForm({ ...editForm, value: e.target.value })}
                                                        />
                                                    ) : (
                                                        item.value
                                                    )}
                                                </td>
                                                <td>
                                                    {editingId === item.id ? (
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm"
                                                            value={editForm.label}
                                                            onChange={e => setEditForm({ ...editForm, label: e.target.value })}
                                                        />
                                                    ) : (
                                                        item.label
                                                    )}
                                                </td>
                                                <td>
                                                    <span className={`badge ${item.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                                        {item.is_active ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td>
                                                    {editingId === item.id ? (
                                                        <>
                                                            <button
                                                                className="btn btn-sm btn-success me-1"
                                                                onClick={() => handleUpdate(item.id)}
                                                                disabled={saving}
                                                            >
                                                                {saving ? '...' : 'Save'}
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-secondary"
                                                                onClick={handleCancelEdit}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button
                                                                className="btn btn-sm btn-outline-primary me-1"
                                                                onClick={() => handleEdit(item)}
                                                            >
                                                                Edit
                                                            </button>
                                                            <button
                                                                className={`btn btn-sm ${item.is_active ? 'btn-outline-warning' : 'btn-outline-success'}`}
                                                                onClick={() => handleToggleActive(item)}
                                                            >
                                                                {item.is_active ? 'Deactivate' : 'Activate'}
                                                            </button>
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {values.length === 0 && !showAddForm && (
                                            <tr>
                                                <td colSpan="5" className="text-center text-muted py-4">
                                                    No values found for this category.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default DropdownManager;
